/**
 * 1994 phosphor boot: the fake memory count-up IS the real warmup time, and
 * the first tap doubles as the WebAudio unlock (onFirstTap runs synchronously
 * inside the pointerdown handler — the caller resumes the AudioContext there).
 */
export interface BootStrings {
  studioLine: string; // "XYZ (TM) SYSTEM"
  loadingLine: string; // "LOADING XYZ v1.0"
  title: string;
  touchLine: string; // "TOUCH TO BEGIN"
  graphicUrl?: string; // optional title-screen art; falls back to the wordmark
}

const SKIP_KEY = 'tg.bootSeen';

export function runBootSequence(
  root: HTMLElement,
  s: BootStrings,
  onFirstTap: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    const seen = localStorage.getItem(SKIP_KEY) === '1';

    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'background:#050805',
      'z-index:50',
      'pointer-events:auto',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'transition:opacity 0.45s ease',
      'touch-action:none',
    ].join(';');

    const term = document.createElement('pre');
    term.style.cssText = [
      'color:#33FF66',
      'font:13px/1.7 Consolas,monospace',
      'margin:0 24px',
      'text-shadow:0 0 6px rgba(51,255,102,0.55)',
      'white-space:pre-wrap',
      'align-self:flex-start',
      'padding-left:18px',
    ].join(';');
    overlay.appendChild(term);
    root.appendChild(overlay);

    let phase: 'counting' | 'title' = 'counting';
    let countTimer = 0;

    const showTitle = (): void => {
      phase = 'title';
      window.clearInterval(countTimer);
      term.remove();
      const card = document.createElement('div');
      card.style.cssText =
        'display:flex;flex-direction:column;align-items:center;text-align:center;user-select:none;gap:8px;';

      const title = document.createElement('div');
      title.textContent = s.title.toUpperCase();
      title.style.cssText = [
        'color:#F2D024',
        'font:bold 34px/1.2 Consolas,monospace',
        'letter-spacing:6px',
        'text-shadow:3px 3px 0 #5B4A12, 0 0 18px rgba(242,208,36,0.35)',
      ].join(';');

      const touch = document.createElement('div');
      touch.textContent = s.touchLine;
      touch.style.cssText = [
        'color:#D4D0C8',
        'font:13px/1 Consolas,monospace',
        'letter-spacing:3px',
        'margin-top:20px',
        'animation:tg-blink 1.2s steps(2) infinite',
      ].join(';');

      const style = document.createElement('style');
      style.textContent = '@keyframes tg-blink { 50% { opacity: 0.15; } }';
      card.append(style);

      // optional title art: shows above a smaller wordmark, with a soft drop
      // shadow to match its sticker outline. If the file is absent it simply
      // never appears and the big wordmark carries the screen.
      if (s.graphicUrl) {
        const art = document.createElement('img');
        art.alt = '';
        art.decoding = 'async';
        art.style.cssText = [
          'max-width:80vw',
          'max-height:46vh',
          'width:auto',
          'height:auto',
          'object-fit:contain',
          'filter:drop-shadow(0 6px 10px rgba(0,0,0,0.55))',
          'opacity:0',
          'transition:opacity 0.5s ease',
        ].join(';');
        art.addEventListener('load', () => {
          art.style.opacity = '1';
          // art is the hero: shrink the wordmark to a subtitle
          title.style.font = 'bold 22px/1.2 Consolas,monospace';
          title.style.letterSpacing = '5px';
        });
        art.addEventListener('error', () => art.remove());
        art.src = s.graphicUrl;
        card.append(art);
      }

      card.append(title, touch);
      overlay.appendChild(card);
    };

    if (seen) {
      showTitle();
    } else {
      const lines: string[] = [`${s.studioLine}`, ''];
      let mem = 0;
      const render = (): void => {
        term.textContent = lines.join('\n') + (phase === 'counting' ? '\n█' : '');
      };
      lines.push('CHECKING MEMORY ...');
      render();
      countTimer = window.setInterval(() => {
        mem += 64;
        if (mem <= 640) {
          lines[2] = `CHECKING MEMORY ... ${mem}K OK`;
          render();
        } else {
          lines.push(s.loadingLine, '');
          render();
          window.clearInterval(countTimer);
          window.setTimeout(showTitle, 650);
        }
      }, 90);
    }

    overlay.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (phase === 'counting') {
        showTitle(); // impatient tap skips the count
        return;
      }
      onFirstTap(); // synchronous: audio unlock happens inside the gesture
      localStorage.setItem(SKIP_KEY, '1');
      overlay.style.opacity = '0';
      overlay.style.pointerEvents = 'none';
      window.setTimeout(() => {
        overlay.remove();
        resolve();
      }, 460);
    });
  });
}
